import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { MessageObject } from '../../types/inbox'
import { api } from '../../utils/api-client'
import { queryKeys } from './keys'

// Rows per request. The backend reads the page size off the record_list payload
// as `pages` (not a total-page count, which is what `pages` means on
// articles.php — see useBlogs).
export const INBOX_PAGE_SIZE = 20

interface InboxPage {
  messages: MessageObject[]
  page: number
  hasMore: boolean
}

// Fetches one page of the message record list. Exported as a test seam.
export const fetchInboxPage = async (
  page: number,
  signal?: AbortSignal,
): Promise<InboxPage> => {
  const { data } = await api.post<ApiResponse<MessageObject[]>>(
    '/ajax_data.php',
    {
      type: 'record_list',
      record_type: 'message',
      version: 2,
      page,
      pages: INBOX_PAGE_SIZE,
    },
    { signal },
  )

  // A non-1 status puts an error string in `info` rather than rows, so guard the
  // shape instead of trusting the envelope.
  const messages =
    data.status === 1 && Array.isArray(data.info) ? data.info : []

  return {
    messages,
    page,
    // The response carries no total, so a short page is the end of the list. A
    // full last page costs one extra request that comes back empty and stops
    // paging — the alternative (guessing it was the end) would hide messages.
    hasMore: messages.length === INBOX_PAGE_SIZE,
  }
}

export function useInboxMessages(options?: { enabled?: boolean }) {
  const username = useBoundStore(s => s.user.username)

  const query = useInfiniteQuery<InboxPage>({
    queryKey: [...queryKeys.inbox_messages, username],
    queryFn: ({ signal, pageParam = 1 }) =>
      fetchInboxPage(pageParam as number, signal),
    getNextPageParam: last => (last.hasMore ? last.page + 1 : undefined),
    // There is no inbox without an account: record_list is only meaningful for
    // a signed-in member (the request interceptor has no credentials to attach
    // otherwise), so nothing is requested until the username is there. The
    // username is part of the key, so it refetches once the silent re-login
    // populates the store. `?? true` keeps react-query's own default for a
    // caller that passes nothing.
    enabled: !!username && (options?.enabled ?? true),
    // Reading a message changes its row, so never serve a cached list on the
    // way back from one (the global default is a 5-minute staleTime). Callers
    // pause this query while a message is open; re-enabling it refetches the
    // pages already loaded, so rows keep their place and pick up read state.
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  // Flattened once per fetch, not per render: the inbox page feeds this list to
  // a memoised renderer, and a fresh array on every render would remount every
  // row (dropping each one's local read state).
  const messages = useMemo(
    () => query.data?.pages.flatMap(p => p.messages) ?? [],
    [query.data],
  )

  return {
    messages,
    isFetching: query.isFetching,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  }
}
